/**
 * Central de Apontamentos por Lote (Legado › Área do PCP).
 *
 * Gera, mostra e imprime as folhas A4 de apontamento de cada setor por lote,
 * lendo a planilha Gerenciador de Produção (aba GERENCIAL, só leitura) pela
 * mesma camada das outras telas do Legado. As datas são as mesmas das abas
 * APONTAMENTO da planilha. O histórico de impressão fica no ERP.
 *
 * Fluxo-alvo: abrir → o lote recém-otimizado está no topo de "Não impressos"
 * → marcar → Imprimir. Deep links: ?lote=508,509&setores=compras,maquinas
 * &acao=imprimir.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiPrinter } from "react-icons/fi";

import ExportMenu from "../../components/ExportMenu";
import { EmptyStateErro } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { useDados } from "../../context/DadosContext";
import { useToast } from "../../context/ToastContext";
import useCalendarioUnidade from "../../hooks/useCalendarioUnidade";
import useLogoUnidade from "../../hooks/useLogoUnidade";
import {
  AJUSTE_INICIAL,
  CODIGO_AVISO,
  FILTRO_PADRAO,
  IDS_DA_MATRIZ,
  PRESETS,
  agruparLotes,
  agruparPorCliente,
  buscarLotes,
  filtrarLotes,
  formatarData,
  loteCabeNumaFolha,
  MAXIMO_DE_AMBIENTES_POR_FOLHA,
  montarFolhas,
  nomeDoArquivo,
  proximaVia,
  seloDoLote,
  setorPorId,
  setorSeAplica,
  validarLote,
} from "../../utils/apontamentos";
import { hojeIso } from "../../utils/capacidade/calendario";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";
import AreaImpressao from "./AreaImpressao";
import { gerarPdfDasFolhas, salvarImagensDasFolhas } from "./exportacao";
import { FontesDaFolha, aguardarFontes } from "./folha/fontes";
import Historico from "./Historico";
import ListaLotes from "./ListaLotes";
import MatrizImpressao from "./MatrizImpressao";
import PreviaFolhas from "./PreviaFolhas";
import { Abas, Aviso, BotaoPrimario, Coluna, Contador, Layout, Pagina, Topo } from "./styles";
import useHistoricoImpressoes from "./useHistoricoImpressoes";

export const ROTA_APONTAMENTOS = "/legado/apontamentos";
const FORMATOS_DE_EXPORTACAO = ["pdf", "png", "jpg"];

function chaveDaUltimaSelecao(usuarioId) {
  return `apontamentos:setores:${usuarioId ?? "anonimo"}`;
}

function lerUltimaSelecao(usuarioId) {
  try {
    const salvo = JSON.parse(getItemTenant(chaveDaUltimaSelecao(usuarioId)) || "null");
    return Array.isArray(salvo) ? salvo.filter((id) => IDS_DA_MATRIZ.includes(id)) : null;
  } catch (erro) {
    // Preferência corrompida não pode travar a tela: volta ao preset Todos.
    console.warn("Seleção de setores salva inválida; usando Todos.", erro);
    return null;
  }
}

function doisQuadros() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

function ehCampoDeTexto(alvo) {
  return alvo instanceof HTMLElement && (alvo.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName));
}

/** Muda quando o conteúdo da folha muda (outra versão da planilha). */
function assinaturaDaFolha(folha) {
  return folha.tipo === "setor" ? JSON.stringify(folha.linhas.map((linha) => linha.celulas)) : folha.chave;
}

/** O que vai para o histórico: um item por lote+setor que sai. */
function itensDoRegistro(folhas) {
  const vistos = new Map();
  folhas.forEach((folha) => {
    const chave = `${folha.lote.chave}|${folha.setor.id}`;
    if (!vistos.has(chave)) {
      vistos.set(chave, {
        lote_chave: folha.lote.chave,
        lote_rotulo: folha.lote.rotulo,
        setor: folha.setor.id,
        otimizacao: folha.lote.otimizacao,
      });
    }
  });
  return [...vistos.values()];
}

export default function Apontamentos() {
  const { dados, erro, initialLoading } = useDados();
  const { usuario } = useAuth();
  const toast = useToast();
  const { naoUteis } = useCalendarioUnidade();
  const historico = useHistoricoImpressoes();
  const logoDaUnidade = useLogoUnidade();
  const [params, setParams] = useSearchParams();
  const aba = params.get("aba") === "historico" ? "historico" : "imprimir";
  const buscaRef = useRef(null);

  const hoje = useMemo(() => hojeIso(), []);
  const [filtro, setFiltro] = useState(FILTRO_PADRAO);
  const [busca, setBusca] = useState("");
  const [selecao, setSelecao] = useState(() => new Map());
  const [ajustes, setAjustes] = useState({});
  const [viasConfirmadas, setViasConfirmadas] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [imprimirAoCarregar, setImprimirAoCarregar] = useState(false);
  const deepLinkAplicado = useRef(false);

  const lotes = useMemo(() => agruparLotes(Array.isArray(dados) ? dados : []), [dados]);
  const { resumoPorLote } = historico;
  const selos = useMemo(() => new Map(lotes.map((lote) => [lote.chave, seloDoLote(lote, resumoPorLote)])), [lotes, resumoPorLote]);

  const aplicaveis = useMemo(() => {
    const mapa = new Map();
    lotes.forEach((lote) => {
      mapa.set(lote.chave, new Set(IDS_DA_MATRIZ.filter((id) => id === "carregamento"
        || (loteCabeNumaFolha(lote) && setorSeAplica(setorPorId(id), lote)))));
    });
    return mapa;
  }, [lotes]);
  const aplicavel = useCallback((lote, setorId) => Boolean(aplicaveis.get(lote.chave)?.has(setorId)), [aplicaveis]);
  /** Por que a célula da matriz está desabilitada (tooltip). */
  const motivoDesabilitado = useCallback((lote, setorId) => {
    if (!loteCabeNumaFolha(lote)) {
      return `Lote com ${lote.ambientes.length} ambientes: o limite é ${MAXIMO_DE_AMBIENTES_POR_FOLHA} por folha`;
    }
    return `Sem itens de ${setorPorId(setorId).curto} neste lote`;
  }, []);

  const noFiltro = useMemo(() => filtrarLotes(lotes, filtro, { resumoPorLote, hoje }), [lotes, filtro, resumoPorLote, hoje]);
  const visiveis = useMemo(() => buscarLotes(noFiltro, busca), [noFiltro, busca]);
  const grupos = useMemo(() => agruparPorCliente(visiveis), [visiveis]);
  const selecionados = useMemo(() => new Set(selecao.keys()), [selecao]);

  // ------------------------------------------------------------ seleção

  const setoresPadrao = useCallback(
    (lote) => (lerUltimaSelecao(usuario?.id) || PRESETS.todos).filter((id) => aplicavel(lote, id)),
    [usuario?.id, aplicavel],
  );

  const lembrar = useCallback((setores) => {
    setItemTenant(chaveDaUltimaSelecao(usuario?.id), JSON.stringify([...setores]));
  }, [usuario?.id]);

  const alternarLote = useCallback((lote) => {
    setSelecao((atual) => {
      const proxima = new Map(atual);
      if (proxima.has(lote.chave)) proxima.delete(lote.chave);
      else proxima.set(lote.chave, new Set(setoresPadrao(lote)));
      return proxima;
    });
  }, [setoresPadrao]);

  const alternarCliente = useCallback((lotesDoCliente, marcar) => {
    setSelecao((atual) => {
      const proxima = new Map(atual);
      lotesDoCliente.forEach((lote) => {
        if (!marcar) proxima.delete(lote.chave);
        else if (!proxima.has(lote.chave)) proxima.set(lote.chave, new Set(setoresPadrao(lote)));
      });
      return proxima;
    });
  }, [setoresPadrao]);

  const definirCelulas = useCallback((mudancas) => {
    const proxima = new Map(selecao);
    let ultima = null;
    mudancas.forEach(({ lote, setorId, valor }) => {
      if (!aplicavel(lote, setorId)) return;
      const setores = new Set(proxima.get(lote.chave) || []);
      if (valor) setores.add(setorId);
      else setores.delete(setorId);
      proxima.set(lote.chave, setores);
      ultima = setores;
    });
    setSelecao(proxima);
    if (ultima) lembrar(ultima);
  }, [selecao, aplicavel, lembrar]);

  const aplicarPreset = useCallback((id) => {
    const ids = PRESETS[id];
    setSelecao((atual) => new Map([...atual.keys()].map((chave) => {
      const lote = lotes.find((l) => l.chave === chave);
      return [chave, new Set(lote ? ids.filter((setorId) => aplicavel(lote, setorId)) : [])];
    })));
    lembrar(ids);
  }, [lotes, aplicavel, lembrar]);

  // ----------------------------------------------------------- deep link

  useEffect(() => {
    if (deepLinkAplicado.current || !lotes.length) return;
    deepLinkAplicado.current = true;
    const pedidos = (params.get("lote") || "").split(",").map((v) => v.trim()).filter(Boolean);
    if (!pedidos.length) return;
    const setoresDoLink = (params.get("setores") || "").split(",").map((v) => v.trim()).filter((id) => IDS_DA_MATRIZ.includes(id));
    const escolhidos = lotes.filter((lote) => pedidos.includes(lote.numero) || pedidos.includes(lote.chave));
    if (!escolhidos.length) {
      toast.error(`Lote ${pedidos.join(", ")} não encontrado na planilha (ou ainda sem otimização).`);
      return;
    }
    setFiltro("todos");
    setSelecao(new Map(escolhidos.map((lote) => [
      lote.chave,
      new Set((setoresDoLink.length ? setoresDoLink : PRESETS.todos).filter((id) => aplicavel(lote, id))),
    ])));
    if (params.get("acao") === "imprimir") setImprimirAoCarregar(true);
  }, [lotes, params, aplicavel, toast]);

  // ------------------------------------------------------------- folhas

  const selecaoEmOrdem = useMemo(
    () => lotes
      .filter((lote) => selecao.has(lote.chave))
      .map((lote) => ({ lote, setores: [...selecao.get(lote.chave)] })),
    [lotes, selecao],
  );

  const folhas = useMemo(() => montarFolhas(selecaoEmOrdem), [selecaoEmOrdem]);

  // Ajuste de cada folha (degraus medidos no DOM), guardado com a assinatura
  // do conteúdo: se a planilha mudou a folha, o ajuste velho é descartado.
  const assinaturas = useMemo(() => new Map(folhas.map((folha) => [folha.chave, assinaturaDaFolha(folha)])), [folhas]);
  const ajusteDe = useCallback((folha) => {
    const guardado = ajustes[folha.chave];
    return guardado && guardado.assinatura === assinaturas.get(folha.chave) ? guardado.ajuste : AJUSTE_INICIAL;
  }, [ajustes, assinaturas]);
  const aoAjustar = useCallback((mudancas) => setAjustes((atual) => {
    const proximo = { ...atual };
    Object.entries(mudancas).forEach(([chave, ajuste]) => {
      proximo[chave] = { ajuste, assinatura: assinaturas.get(chave) };
    });
    return proximo;
  }), [assinaturas]);

  const vias = useMemo(() => {
    const data = formatarData(hoje);
    const mapa = {};
    folhas.forEach((folha) => {
      const chave = `${folha.lote.chave}|${folha.setor.id}`;
      const numero = viasConfirmadas?.[chave] ?? proximaVia(folha.lote, folha.setor.id, resumoPorLote);
      mapa[chave] = { numero, data };
    });
    return mapa;
  }, [folhas, viasConfirmadas, resumoPorLote, hoje]);

  const { gruposDaPrevia, celulasComAviso } = useMemo(() => {
    const celulas = new Map();
    const previa = selecaoEmOrdem.map(({ lote, setores }) => {
      const doLote = folhas.filter((folha) => folha.lote.chave === lote.chave);
      const avisos = [
        // O limite de 10 ambientes aparece sempre: sem ele a pessoa marcaria o
        // lote e não veria folha nenhuma sem saber por quê.
        ...validarLote(lote, { hoje, naoUteis, setores, selo: selos.get(lote.chave) })
          .filter((aviso) => aviso.codigo === CODIGO_AVISO.LOTE_ACIMA_DO_LIMITE || aviso.setores.some((id) => setores.includes(id))),
        ...doLote
          .filter((folha) => ajusteDe(folha).escala < 1)
          .map((folha) => ({
            codigo: "letra_reduzida",
            setores: [folha.setor.id],
            celulas: [],
            mensagem: ajusteDe(folha).naoCabe
              ? `Lote ${lote.rotulo}: a folha de ${folha.setor.titulo} não coube num papel nem com a letra mínima. Encurte as descrições na planilha`
              : `Lote ${lote.rotulo}: a folha de ${folha.setor.titulo} saiu com a letra reduzida para caber num papel`,
          })),
      ];
      celulas.set(lote.chave, new Set(avisos.flatMap((aviso) => aviso.celulas.map((c) => `${c.ambiente}|${c.coluna}`))));
      return { lote, avisos, folhas: doLote };
    });
    return { gruposDaPrevia: previa, celulasComAviso: celulas };
  }, [selecaoEmOrdem, folhas, hoje, naoUteis, selos, ajusteDe]);

  const lotesSelecionados = useMemo(() => selecaoEmOrdem.map(({ lote }) => lote), [selecaoEmOrdem]);
  const logo = useMemo(() => ({ src: logoDaUnidade.logo, nome: logoDaUnidade.nomeUnidade }), [logoDaUnidade.logo, logoDaUnidade.nomeUnidade]);

  // -------------------------------------------------- registrar e sair

  /** Registra no histórico; se o ERP não responder, a pessoa decide se sai mesmo assim. */
  const registrar = useCallback(async (formato) => {
    try {
      const confirmadas = await historico.registrar(formato, itensDoRegistro(folhas));
      setViasConfirmadas(confirmadas);
      return true;
    } catch (falha) {
      // eslint-disable-next-line no-alert
      return window.confirm(
        `Não foi possível registrar no histórico (${falha?.message || "erro do servidor"}). `
        + "As folhas vão sair sem a marca de via. Continuar mesmo assim?",
      );
    }
  }, [historico, folhas]);

  const imprimir = useCallback(async () => {
    if (!folhas.length) {
      toast.error("Marque ao menos um lote e um setor.");
      return;
    }
    setOcupado(true);
    try {
      if (!(await registrar("impressao"))) return;
      await aguardarFontes();
      await doisQuadros();
      window.print();
    } finally {
      setViasConfirmadas(null);
      setOcupado(false);
    }
  }, [folhas, registrar, toast]);

  const exportar = useCallback(async (formato) => {
    if (!folhas.length) {
      toast.error("Marque ao menos um lote e um setor.");
      return;
    }
    setOcupado(true);
    try {
      if (!(await registrar(formato === "pdf" ? "pdf" : "imagem"))) return;
      await doisQuadros();
      if (formato === "pdf") {
        await gerarPdfDasFolhas(folhas, nomeDoArquivo({ lotes: lotesSelecionados, hoje, extensao: "pdf" }));
      } else {
        await salvarImagensDasFolhas(folhas, formato, (folha) => nomeDoArquivo({
          lotes: [folha.lote],
          hoje,
          extensao: formato,
          detalhe: folha.setor.curto,
        }));
      }
    } catch (falha) {
      toast.error(falha?.message || "Não foi possível exportar as folhas.");
    } finally {
      setViasConfirmadas(null);
      setOcupado(false);
    }
  }, [folhas, registrar, lotesSelecionados, hoje, toast]);

  useEffect(() => {
    if (!imprimirAoCarregar || !folhas.length || !historico.carregado) return;
    setImprimirAoCarregar(false);
    const semAcao = new URLSearchParams(params);
    semAcao.delete("acao");
    setParams(semAcao, { replace: true });
    imprimir();
  }, [imprimirAoCarregar, folhas.length, historico.carregado, imprimir, params, setParams]);

  // ------------------------------------------------------------- atalhos

  useEffect(() => {
    if (aba !== "imprimir") return undefined;
    const tecla = (evento) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === "p") {
        evento.preventDefault();
        imprimir();
        return;
      }
      if (ehCampoDeTexto(evento.target) || evento.ctrlKey || evento.metaKey || evento.altKey) return;
      if (evento.key === "/") {
        evento.preventDefault();
        buscaRef.current?.focus();
      } else if (evento.key === "t" || evento.key === "T") {
        aplicarPreset("todos");
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aba, imprimir, aplicarPreset]);

  // --------------------------------------------------------------- tela

  if (initialLoading) return <PageLoader scope="apontamentos" />;
  if (erro) return <EmptyStateErro erro={erro} />;

  const semPlanilha = !Array.isArray(dados) || dados.length === 0;
  const totalDeFolhas = folhas.length;

  return (
    <Pagina>
      <FontesDaFolha />
      <Topo>
        <div className="titulo">
          <h1>Apontamentos por lote</h1>
          <p>Folhas A4 de cada setor para os quadros da fábrica, com as datas da planilha Gerenciador de Produção.</p>
        </div>
        {aba === "imprimir" && (
          <div className="acoes">
            <Contador aria-live="polite">{totalDeFolhas === 1 ? "1 folha A4" : `${totalDeFolhas} folhas A4`}</Contador>
            <ExportMenu onExport={exportar} disabled={!totalDeFolhas || ocupado} comImagem somente={FORMATOS_DE_EXPORTACAO} />
            <BotaoPrimario type="button" onClick={imprimir} disabled={!totalDeFolhas || ocupado} title="Imprimir (Ctrl+P)">
              <FiPrinter size={15} aria-hidden="true" /> {ocupado ? "Preparando…" : "Imprimir"}
            </BotaoPrimario>
          </div>
        )}
      </Topo>

      <Abas aria-label="Seções">
        <Link to={ROTA_APONTAMENTOS} aria-current={aba === "imprimir" ? "page" : undefined}>Imprimir</Link>
        <Link to={`${ROTA_APONTAMENTOS}?aba=historico`} aria-current={aba === "historico" ? "page" : undefined}>Histórico</Link>
      </Abas>

      {aba === "historico" ? (
        <Historico listar={historico.listar} />
      ) : (
        <>
          {semPlanilha && (
            <Aviso role="status">
              A planilha Gerenciador de Produção ainda não foi carregada neste aparelho. Abra
              {" "}<Link to="/configuracoes/planilhas">Configurações › Planilhas</Link>.
            </Aviso>
          )}
          {historico.erro && (
            <Aviso role="status">
              O histórico de impressão não respondeu: os selos NOVO/IMPRESSO podem estar incompletos.
            </Aviso>
          )}
          {!logoDaUnidade.logo && logoDaUnidade.carregado && logoDaUnidade.podeEditar && (
            <Aviso role="status">
              A unidade ainda não tem logo: as folhas saem com o nome dela no lugar. Cadastre em
              {" "}<Link to="/configuracoes/empresa">Configurações › Minha empresa</Link>.
            </Aviso>
          )}
          <Layout>
            <Coluna>
              <ListaLotes
                grupos={grupos}
                selecionados={selecionados}
                selos={selos}
                busca={busca}
                onBusca={setBusca}
                filtro={filtro}
                onFiltro={setFiltro}
                onAlternarLote={alternarLote}
                onAlternarCliente={alternarCliente}
                buscaRef={buscaRef}
                totalNoFiltro={noFiltro.length}
              />
              <MatrizImpressao
                lotes={lotesSelecionados}
                selecao={selecao}
                aplicavel={aplicavel}
                motivoDesabilitado={motivoDesabilitado}
                onDefinir={definirCelulas}
                onPreset={aplicarPreset}
              />
            </Coluna>
            <PreviaFolhas grupos={gruposDaPrevia} logo={logo} vias={vias} celulasComAviso={celulasComAviso} ajusteDe={ajusteDe} />
          </Layout>
          <AreaImpressao folhas={folhas} logo={logo} vias={vias} ajusteDe={ajusteDe} onAjustar={aoAjustar} />
        </>
      )}
    </Pagina>
  );
}
